import { Link } from 'react-router-dom';
import SEOMeta from '@/components/feature/SEOMeta';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { aboutData } from '@/mocks/about';

export default function About() {
  const { brand, stats, values, testimonials, contact, faqs } = aboutData;

  const orgSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: brand.name,
    url: 'https://www.nongminyingshi.com',
    description: brand.description,
    foundingDate: String(brand.founded),
    slogan: brand.slogan,
    contactPoint: {
      '@type': 'ContactPoint',
      telephone: contact.phone,
      email: contact.email,
      contactType: 'customer service',
      availableLanguage: 'Chinese',
    },
    address: {
      '@type': 'PostalAddress',
      streetAddress: contact.address,
    },
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map((faq) => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer,
      },
    })),
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: '首页',
        item: 'https://www.nongminyingshi.com',
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: '关于我们',
        item: 'https://www.nongminyingshi.com/about',
      },
    ],
  };

  return (
    <>
      <ScrollToTop />
      <SEOMeta
        title="关于我们 - 农民影视 | 品牌介绍与联系方式"
        description="农民影视是国内领先的免费高清影视在线观看平台，成立于2018年，累计用户超过5,800万。了解农民影视的品牌故事、用户口碑和联系方式。"
        keywords="农民影视,关于农民影视,农民影视简介,影视平台,免费影视,联系方式"
        canonicalUrl="/about"
        ogTitle="关于我们 - 农民影视"
        ogDescription="农民影视是国内领先的免费高清影视在线观看平台，了解品牌故事与联系方式。"
      />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(orgSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }}
      />

      <Navbar />

      <main className="pt-16">
        <Breadcrumb />

        <section className="w-full px-4 md:px-6 py-14 md:py-20 bg-background-100">
          <div className="max-w-[1280px] mx-auto text-center">
            <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-4">
              关于农民影视
            </h1>
            <p className="text-lg text-foreground-600 mb-2 max-w-2xl mx-auto">
              {brand.slogan}
            </p>
            <p className="text-sm text-foreground-500 max-w-xl mx-auto leading-relaxed">
              {brand.description}
            </p>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-5 mt-10">
              {stats.map((stat) => (
                <div key={stat.label} className="p-5 rounded-xl bg-background-50 border border-background-200/60">
                  <div className="text-2xl md:text-3xl font-heading font-bold text-primary-600">{stat.value}</div>
                  <div className="text-xs text-foreground-500 mt-1">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="w-full px-4 md:px-6 py-14 md:py-20 bg-background-50">
          <div className="max-w-[1280px] mx-auto">
            <div className="text-center mb-10">
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900">
                核心价值观
              </h2>
              <p className="text-sm text-foreground-500 mt-1">我们始终坚持的理念</p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {values.map((val) => (
                <div key={val.title} className="group p-6 rounded-xl bg-background-100 border border-background-200/60 hover:border-primary-200 transition-all">
                  <span className="w-12 h-12 flex items-center justify-center rounded-xl bg-primary-100 text-primary-600 group-hover:bg-primary-500 group-hover:text-background-50 transition-colors mb-4">
                    <i className={`${val.icon} text-xl`}></i>
                  </span>
                  <h3 className="text-base font-heading font-semibold text-foreground-900 mb-2">{val.title}</h3>
                  <p className="text-sm text-foreground-600 leading-relaxed">{val.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="w-full px-4 md:px-6 py-14 md:py-20 bg-background-100">
          <div className="max-w-[1280px] mx-auto">
            <div className="text-center mb-10">
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900">
                用户口碑
              </h2>
              <p className="text-sm text-foreground-500 mt-1">听听用户怎么说</p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {testimonials.map((t) => (
                <div key={t.name} className="p-5 rounded-xl bg-background-50 border border-background-200/60">
                  <div className="flex items-center gap-1 mb-3">
                    {Array.from({ length: t.rating }).map((_, i) => (
                      <i key={i} className="ri-star-fill text-accent-500 text-sm"></i>
                    ))}
                  </div>
                  <p className="text-sm text-foreground-700 leading-relaxed mb-4">
                    &ldquo;{t.content}&rdquo;
                  </p>
                  <div className="flex items-center gap-3 pt-3 border-t border-background-200/60">
                    <div className="w-9 h-9 rounded-full bg-primary-100 flex items-center justify-center text-primary-600 text-sm font-bold">
                      {t.name.charAt(0)}
                    </div>
                    <div>
                      <div className="text-sm font-medium text-foreground-900">{t.name}</div>
                      <div className="text-xs text-foreground-500">{t.role}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="w-full px-4 md:px-6 py-14 md:py-20 bg-background-50">
          <div className="max-w-[1280px] mx-auto">
            <div className="text-center mb-10">
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900">
                联系我们
              </h2>
              <p className="text-sm text-foreground-500 mt-1">欢迎随时与我们取得联系</p>
            </div>
            <div className="max-w-2xl mx-auto grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
              <div className="p-5 rounded-xl bg-background-100 border border-background-200/60 text-center">
                <span className="w-10 h-10 flex items-center justify-center mx-auto rounded-lg bg-accent-100 text-accent-600 mb-3">
                  <i className="ri-mail-line text-lg"></i>
                </span>
                <div className="text-xs text-foreground-500 mb-1">电子邮箱</div>
                <div className="text-sm font-medium text-foreground-900">{contact.email}</div>
              </div>
              <div className="p-5 rounded-xl bg-background-100 border border-background-200/60 text-center">
                <span className="w-10 h-10 flex items-center justify-center mx-auto rounded-lg bg-accent-100 text-accent-600 mb-3">
                  <i className="ri-phone-line text-lg"></i>
                </span>
                <div className="text-xs text-foreground-500 mb-1">客服热线</div>
                <div className="text-sm font-medium text-foreground-900">{contact.phone}</div>
              </div>
              <div className="p-5 rounded-xl bg-background-100 border border-background-200/60 text-center">
                <span className="w-10 h-10 flex items-center justify-center mx-auto rounded-lg bg-accent-100 text-accent-600 mb-3">
                  <i className="ri-wechat-line text-lg"></i>
                </span>
                <div className="text-xs text-foreground-500 mb-1">微信公众号</div>
                <div className="text-sm font-medium text-foreground-900">{contact.wechat}</div>
              </div>
              <div className="p-5 rounded-xl bg-background-100 border border-background-200/60 text-center">
                <span className="w-10 h-10 flex items-center justify-center mx-auto rounded-lg bg-accent-100 text-accent-600 mb-3">
                  <i className="ri-time-line text-lg"></i>
                </span>
                <div className="text-xs text-foreground-500 mb-1">工作时间</div>
                <div className="text-sm font-medium text-foreground-900">{contact.workingHours}</div>
              </div>
            </div>
          </div>
        </section>

        <section className="w-full px-4 md:px-6 py-14 md:py-20 bg-background-100">
          <div className="max-w-[1280px] mx-auto">
            <div className="text-center mb-10">
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900">
                常见问题
              </h2>
              <p className="text-sm text-foreground-500 mt-1">快速解答您的疑问</p>
            </div>
            <div className="max-w-3xl mx-auto space-y-3">
              {faqs.map((faq, index) => (
                <details key={index} className="group rounded-xl bg-background-50 border border-background-200/60 overflow-hidden">
                  <summary className="flex items-center justify-between p-4 md:p-5 cursor-pointer list-none">
                    <span className="text-sm md:text-base font-medium text-foreground-900 pr-4">{faq.question}</span>
                    <span className="w-6 h-6 flex items-center justify-center flex-shrink-0 rounded-full bg-background-200 text-foreground-500 group-open:bg-primary-100 group-open:text-primary-600 transition-colors">
                      <i className="ri-add-line group-open:ri-subtract-line text-sm transition-all"></i>
                    </span>
                  </summary>
                  <div className="px-4 md:px-5 pb-4 md:pb-5">
                    <p className="text-sm text-foreground-600 leading-relaxed">{faq.answer}</p>
                  </div>
                </details>
              ))}
            </div>
          </div>
        </section>

        <section className="w-full px-4 md:px-6 py-12 bg-background-50">
          <div className="max-w-[1280px] mx-auto text-center">
            <h2 className="text-lg font-heading font-bold text-foreground-900 mb-3">
              开始观影之旅
            </h2>
            <p className="text-sm text-foreground-500 mb-5 max-w-md mx-auto">
              加入5,800万用户，在农民影视享受免费高清影视盛宴
            </p>
            <Link
              to="/"
              className="inline-flex items-center gap-1.5 px-8 py-3 bg-primary-500 hover:bg-primary-600 text-background-50 font-medium rounded-full transition-colors text-sm whitespace-nowrap"
            >
              <i className="ri-home-line"></i>
              返回首页
            </Link>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}